import { AppLayout } from '@/components/app-layout';
import { AuthCheck } from '@/components/auth-check.component';
import { FeatureGate } from '@/components/feature-gate.component';
import { Head } from '@/components/head.component';
import { Feature } from '@/constants/features';
import { SearchPageContainer } from '@/containers/search/search-page-container.component';
import {
  createFileRoute,
  useNavigate,
  useRouterState,
  useSearch,
} from '@tanstack/react-router';
import { useCallback } from 'react';
import { z } from 'zod';

const searchParamsSchema = z.object({
  // .catch: an unknown value (stale link, typo) falls back instead of
  // throwing, which crashed the whole page ("This page couldn't load").
  tab: z
    .enum(['people', 'companies'])
    .optional()
    .default('people')
    .catch('people'),
  listId: z.string().optional(),
  /** The sequence Find People was opened from: Add to sequence targets it. */
  sequenceId: z.string().optional(),
  /** Base64-encoded JSON filter object from copilot. Decoded + applied on mount. */
  aiFilters: z.string().optional(),
  /** Base64-encoded JSON filter object representing the user's current
   *  search. Updated whenever the user runs a search so the URL is
   *  shareable / reloadable. Decoded + applied + auto-searched on
   *  mount, same pattern as ``aiFilters``. */
  q: z.string().optional(),
  /** A Copy link code (finder_shared_searches_v1). The page fetches the search
   *  it names and runs it; once that search writes its own ``q``, ``s`` goes. */
  s: z
    .string()
    .regex(/^[A-Za-z0-9_-]{22}$/)
    .optional()
    .catch(undefined),
  /** Kept as text so unsupported MCP versions reach the visible error state. */
  mcp: z.coerce.string().optional(),
  /** Separate from q so older portal builds cannot auto-run an MCP envelope. */
  mcpq: z.string().optional(),
});

declare module '@tanstack/react-router' {
  interface HistoryState {
    /** A search too big for ?q= (a list's 2,500 companies), in q's encoding.
     *  Read like q; the page's next q write replaces the entry and drops it. */
    finderQ?: string;
  }
}

export const Route = createFileRoute('/finder')({
  component: RouteComponent,
  validateSearch: searchParamsSchema,
});

function RouteComponent() {
  const { tab, listId, sequenceId, aiFilters, q, s, mcp, mcpq } = useSearch({
    from: '/finder',
  });
  const stateQ = useRouterState({
    select: (st) => st.location.state?.finderQ,
  });
  const navigate = useNavigate();

  const handleTabChange = useCallback(
    (nextTab: 'people' | 'companies') => {
      navigate({
        to: '/finder',
        search: {
          tab: nextTab,
          listId,
          sequenceId,
          // A link that hasn't written its own q yet (an exhausted trial
          // waiting for a click, a search too long for q) keeps its code.
          ...(mcp === undefined && mcpq === undefined ? { q, s } : {}),
        },
        replace: true,
      });
    },
    [navigate, listId, sequenceId, q, s, mcp, mcpq]
  );

  const handleQueryChange = useCallback(
    (nextQ: string | undefined) => {
      navigate({
        to: '/finder',
        search: { tab, listId, sequenceId, q: nextQ },
        replace: true,
      });
    },
    [navigate, tab, listId, sequenceId]
  );

  return (
    <>
      <Head heading={tab === 'companies' ? 'Find Companies' : 'Find People'} />
      <AuthCheck>
        <AppLayout variant='full-bleed'>
          <FeatureGate feature={Feature.DATA_SOURCE_PEOPLE_SEARCH} previewOnce>
            <SearchPageContainer
              linkedListId={listId}
              linkedSequenceId={sequenceId}
              initialTab={tab}
              onTabChange={handleTabChange}
              aiFilters={aiFilters}
              urlQuery={q ?? stateQ}
              sharedSearchCode={s}
              mcpMarker={mcp}
              mcpQuery={mcpq}
              onQueryChange={handleQueryChange}
            />
          </FeatureGate>
        </AppLayout>
      </AuthCheck>
    </>
  );
}
